import { Component, computed, inject, input, output, signal } from "@angular/core";
import { OnUpgradeArgs, SubscriptionType } from "src/app/models/subscription";
import { SubscriptionService } from "src/app/services/subscription.service";
import { IonIcon, IonBadge, IonSpinner } from "@ionic/angular/standalone";
import { LocalizationModule } from "../../../../modules/localization/localization.module";
import { CurrencyPipe, DatePipe } from "@angular/common";
import { ButtonComponent } from "../../../ui/form/button/button.component";
import { Router } from "@angular/router";
import { ToastService } from "src/app/services/toast.service";
import { AlertController, NavController } from "@ionic/angular";
import { HttpErrorResponse } from "@angular/common/http";
import { ApiService } from "src/app/services/api.service";

@Component({
  selector: "app-upgrade-card",
  templateUrl: "./upgrade-card.component.html",
  styleUrls: ["./upgrade-card.component.scss"],
  standalone: true,
  imports: [IonSpinner, IonBadge, LocalizationModule, IonIcon, CurrencyPipe, ButtonComponent, DatePipe],
})
export class UpgradeCardComponent {
  subscriptionService = inject(SubscriptionService);
  apiService = inject(ApiService);
  router = inject(Router);
  navController = inject(NavController);
  alertCtrl = inject(AlertController);
  toastService = inject(ToastService);
  type = input.required<SubscriptionType>();
  icon = input.required<string>();
  price = input.required<number>();
  pros = input.required<string[]>();
  cons = input.required<string[]>();
  upgradingInProgress = input.required<boolean>();
  onUpgrade = output<OnUpgradeArgs>();
  name = computed(() => this.subscriptionService.subscriptionPricing[this.type()].name);
  processing = signal(false);
  renewsOn = computed(() => {
    const subscription = this.subscriptionService.subscription();
    return subscription?.startDate;
  });
  upgradeStatus = computed<UpgradeArgs>(() => {
    const subscription = this.subscriptionService.subscription();
    const existingSubscriptionType = subscription?.type;
    if (existingSubscriptionType === this.type())
      return { displayText: "Current subscription", direction: "current", renewOn: subscription?.startDate || '', willRenew: subscription?.willRenew ?? false };
    if (!existingSubscriptionType)
      return { displayText: "Continue to payment", direction: "upgrade", renewOn: null, willRenew: false };
    if (existingSubscriptionType < this.type())
      return { displayText: "Upgrade", direction: "upgrade", renewOn: null, willRenew: false };
    if (existingSubscriptionType > this.type())
      return { displayText: "Downgrade", direction: "downgrade", renewOn: null, willRenew: false };
    return { displayText: "Current subscription", direction: "current", renewOn: subscription?.startDate || '', willRenew: subscription?.willRenew };
  });

  onUpgradeClick() {
    if (this.upgradeStatus().direction === "current") return;
    this.onUpgrade.emit({
      subscriptionType: this.type(),
      amount: this.subscriptionService.subscriptionPricing[this.type()].price,
    });
  }

  async cancel() {
    const alert = await this.alertCtrl.create({
      header: "Confirm Cancellation",
      message: "Are you sure you want to cancel your subscription?",
      buttons: [
        {
          text: "No",
          role: "cancel",
        },
        {
          text: "Yes",
          handler: async () => {
            this.processing.set(true);
            try {
              const result = await this.subscriptionService.cancelSubscription();
              this.toastService.info(result.message);
              this.router.navigateByUrl('/dashboard/cancellation-confirmation');
              this.subscriptionService.refreshSubscription();
            } catch (e) {
              const error = e as HttpErrorResponse;
              this.toastService.error(error.error.message);
            } finally {
              this.processing.set(false);
            }
          },
        },
      ],
    });
    await alert.present();
  }

  async renew() {
    const alert = await this.alertCtrl.create({
      header: "Renew subscription?",
      message: "Proceed with subscription renewal?",
      buttons: [
        { text: "Cancel", role: "cancel" },
        {
          text: "Yes",
          handler: async () => {
            const response = await this.apiService.postAsync('/subscription/renew');
            this.toastService.success(response.message);
            this.subscriptionService.refreshSubscription();
          }
        }
      ]
    });
    alert.present();
  }
}

export interface UpgradeArgs {
  displayText: string;
  direction: "upgrade" | "downgrade" | "current";
  willRenew: boolean;
  renewOn: string | null;
}
